/**
 * ConfirmModal.jsx
 * Reusable delete-confirmation modal that matches the VATICA design system.
 *
 * Usage:
 *   const [confirm, setConfirm] = useState(null);
 *
 *   // Trigger:
 *   setConfirm({ message: "Delete this report?", onConfirm: () => doDelete() });
 *
 *   // Render (anywhere in JSX, after all other content):
 *   <ConfirmModal state={confirm} onClose={() => setConfirm(null)} />
 */

import { useEffect, useRef } from "react";

const MODAL_CSS = `
  .cm-backdrop {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.65);
    backdrop-filter: blur(4px);
    z-index: 9999;
    display: flex; align-items: center; justify-content: center;
    padding: 20px;
    animation: cm-fade-in 0.15s ease;
  }
  @keyframes cm-fade-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  .cm-box {
    background: var(--panel, #0f1923);
    border: 1px solid rgba(255,107,138,0.25);
    border-radius: 18px;
    padding: 28px 30px 24px;
    width: 100%;
    max-width: 420px;
    box-shadow: 0 24px 64px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04);
    animation: cm-slide-up 0.18s cubic-bezier(0.34,1.56,0.64,1);
  }
  @keyframes cm-slide-up {
    from { opacity: 0; transform: translateY(16px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0)   scale(1);    }
  }
  .cm-icon-wrap {
    width: 52px; height: 52px;
    border-radius: 14px;
    background: rgba(255,107,138,0.1);
    border: 1px solid rgba(255,107,138,0.2);
    display: flex; align-items: center; justify-content: center;
    font-size: 22px;
    margin-bottom: 18px;
  }
  .cm-title {
    font-size: 16px;
    font-weight: 700;
    color: var(--text, #e8edf5);
    margin-bottom: 8px;
    line-height: 1.3;
  }
  .cm-message {
    font-size: 13px;
    color: var(--muted-light, #8a9ab5);
    line-height: 1.6;
    margin-bottom: 6px;
  }
  .cm-warning {
    font-size: 11px;
    color: var(--error, #ff6b8a);
    margin-top: 10px;
    display: flex; align-items: center; gap: 6px;
    opacity: 0.85;
  }
  .cm-actions {
    display: flex; gap: 10px; margin-top: 24px;
  }
  .cm-btn-cancel {
    flex: 1;
    padding: 11px 0;
    border-radius: 10px;
    border: 1px solid var(--border, rgba(255,255,255,0.08));
    background: transparent;
    color: var(--muted-light, #8a9ab5);
    font-family: 'Outfit', sans-serif;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.18s;
  }
  .cm-btn-cancel:hover {
    background: rgba(255,255,255,0.05);
    color: var(--text, #e8edf5);
    border-color: rgba(255,255,255,0.15);
  }
  .cm-btn-confirm {
    flex: 1;
    padding: 11px 0;
    border-radius: 10px;
    border: none;
    background: linear-gradient(135deg, #e85575, #c73f5a);
    color: #fff;
    font-family: 'Outfit', sans-serif;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.18s;
    box-shadow: 0 4px 16px rgba(232,85,117,0.3);
  }
  .cm-btn-confirm:hover {
    background: linear-gradient(135deg, #f0637e, #d94d6b);
    box-shadow: 0 6px 20px rgba(232,85,117,0.45);
    transform: translateY(-1px);
  }
  .cm-btn-confirm:active { transform: translateY(0); }
`;

export default function ConfirmModal({ state, onClose }) {
  const cancelRef = useRef(null);

  // Focus cancel button when modal opens (safer default)
  useEffect(() => {
    if (state) {
      setTimeout(() => cancelRef.current?.focus(), 50);
    }
  }, [state]);

  // Close on Escape
  useEffect(() => {
    if (!state) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [state, onClose]);

  if (!state) return null;

  const { title, message, onConfirm, confirmLabel = "Delete", icon = "🗑" } = state;

  function handleConfirm() {
    onClose();
    onConfirm();
  }

  return (
    <>
      <style>{MODAL_CSS}</style>
      <div className="cm-backdrop" onClick={onClose}>
        <div className="cm-box" onClick={e => e.stopPropagation()}>
          <div className="cm-icon-wrap">{icon}</div>
          {title && <div className="cm-title">{title}</div>}
          <div className="cm-message">{message}</div>
          <div className="cm-warning">⚠ This action cannot be undone.</div>
          <div className="cm-actions">
            <button ref={cancelRef} className="cm-btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button className="cm-btn-confirm" onClick={handleConfirm}>
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
